import type { PreInscricaoForm } from 'schemas/pre-inscricao.schema';
import type { Pessoa } from 'types/pessoa';
import { DirecionadorTransversal, SEMESTRES_2026 } from 'utils/direcionadores';
import { dedupParticipantes } from 'utils/participantes-dedup';
import { CategoriaPreInscricao, getNomeExibicao } from 'utils/pessoa';

/** Iniciativa já gravada, como volta do `/find/entregas_pre_inscricao` (modo edição). */
export interface IniciativaPreInscricao {
  _id?: string;
  title?: string;
  description?: string;
  result?: string;
  categoryPremio?: string;
  categoria_ecbb?: string;
  categoria_conexao?: string;
  semestre?: number;
  direcionador?: { _id: string; nome: string };
  unidade_validadora?: { prefixo: number; nome: string };
  owner?: string;
  team?: { _id?: string; name?: string }[];
  created?: unknown;
  approval_log?: unknown[];
}

const CATEGORIAS: CategoriaPreInscricao[] = ['CONEXAO', 'PILAR', 'ELBB'];

/** A API devolve datas como número; regravar assim trocaria o tipo do campo. */
const paraData = (valor: unknown) =>
  typeof valor === 'number' ? { $date: new Date(valor).toISOString() } : valor;

/**
 * Documento da exclusão lógica. O PUT substitui o documento inteiro, então tira
 * o que o aggregate acrescenta só para exibição e preserva o tipo das datas.
 */
export function montarExclusaoPreInscricao(entrega: any, agora: Date = new Date()) {
  const { target, now, owner_name, owner_name_guerra, ...documento } = entrega;

  return {
    ...documento,
    start: paraData(entrega.start),
    end: paraData(entrega.end),
    created: paraData(entrega.created),
    updated: { $date: agora.toISOString() },
    active: false,
  };
}

/**
 * Documento gravado em COLECAO_INICIATIVAS. `direcionador` é o item escolhido
 * da lista de `direcionadores_transversais`. Na edição o PUT substitui o documento
 * inteiro, então os campos de controle do original (`created`, `approval_log`)
 * são mantidos.
 */
export function montarPayloadPreInscricao(
  form: PreInscricaoForm,
  pessoa: Pessoa,
  direcionador: DirecionadorTransversal,
  original?: IniciativaPreInscricao,
  agora: Date = new Date()
) {
  const semestre = SEMESTRES_2026.find((s) => s.valor === form.semestre);
  const unidade = form.unidade!;
  const lotacao = pessoa.lotacao;

  const team = dedupParticipantes([
    { _id: pessoa._id, name: getNomeExibicao(pessoa) ?? pessoa.nome },
    ...form.participantes.map((p) => ({ _id: p._id, name: p.nome })),
  ]);

  return {
    ...(original?._id ? { _id: original._id } : {}),
    title: form.titulo.trim(),
    description: form.descricao.trim(),
    result: form.resultado.trim(),
    categoryPremio: form.categoria,
    ...(form.categoria === 'ELBB' ? { categoria_ecbb: form.categoriaEcbb } : {}),
    ...(form.categoria === 'CONEXAO' ? { categoria_conexao: form.categoriaConexao } : {}),
    direcionador: { _id: direcionador._id, nome: direcionador.nome },
    semestre: Number(form.semestre),
    start: { $date: semestre?.start },
    end: { $date: semestre?.end },
    unidade_validadora: { prefixo: unidade.prefixo, nome: unidade.nome },
    original_manager: unidade.prefixo,
    owner: pessoa._id,
    owner_lotacao: lotacao
      ? { prefixo: lotacao.prefixo, nome: lotacao.nome, super: lotacao.ut ?? null, diretoria: lotacao.ue ?? null }
      : null,
    team,
    status: 'aguardando',
    pre_inscricao: true,
    edicao: 2027,
    created: paraData(original?.created) ?? { $date: agora.toISOString() },
    updated: { $date: agora.toISOString() },
    ...(original?.approval_log ? { approval_log: original.approval_log } : {}),
  };
}

/** Valores do formulário a partir de uma iniciativa gravada (modo edição). */
export function formDaIniciativa(iniciativa: IniciativaPreInscricao): PreInscricaoForm {
  const categoria = CATEGORIAS.find((c) => c === iniciativa.categoryPremio) ?? 'CONEXAO';
  return {
    categoria,
    titulo: iniciativa.title ?? '',
    semestre: iniciativa.semestre === 2 ? '2' : '1',
    unidade: iniciativa.unidade_validadora ?? null,
    categoriaEcbb: iniciativa.categoria_ecbb ?? '',
    categoriaConexao: iniciativa.categoria_conexao ?? '',
    // Iniciativa sem direcionador abre sem seleção e exige a escolha (PRE-16).
    direcionadorId: iniciativa.direcionador?._id ?? '',
    participantes: (iniciativa.team ?? [])
      .filter((membro) => membro._id && membro._id !== iniciativa.owner)
      .map((membro) => ({ _id: membro._id!, nome: membro.name ?? membro._id! })),
    descricao: iniciativa.description ?? '',
    resultado: iniciativa.result ?? '',
  };
}
